import React, { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@components/ui/Card';
import { EmptyState } from '@assets/images';
import { Skeleton } from '@components/ui/Skeleton';
import { QuoteCard, QuoteFilters, QuoteDetailsDrawer } from '../components';
import { useQuotesCatchup } from '../hooks/useQuotesCatchup';

/**
 * QuotesList Page
 * Main page displaying all quotes with filtering functionality
 */
const QuotesList = () => {

  // State management
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  // Background catch-up: resolve IN_TRANSIT flights that landed while app was closed
  useQuotesCatchup();

  // Ensure user profile is loaded when authenticated but profile is missing
  useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  // Get profile ID (API may return as 'id' or 'userprofile_id')
  const profileId = userProfile?.userprofile_id;

  /**
   * Use React Query to fetch quotes with API-based filtering
   * - Fetches based on activeFilter status
   * - Refetches when activeFilter changes
   */
  const { data: quotesData, isLoading: isQuotesLoading } = useQuery({
    queryKey: ['quotes', activeFilter, profileId],
    queryFn: async () => {
      // For draft filter, fetch all quotes and filter client-side to include null status
      // For other filters, pass status to API

      const filters = {
        ...({created_by: profileId }),
      };

      const status = activeFilter === 'all' ? null : activeFilter;
      const data = await apiClient.getQuotes(0, 100, { ...filters, ...(status && { status }) });
      
      // Filter quotes that have at least one shortlisted bid option
      let filteredQuotes = data.filter(quote => {
        const hasShortlisted = quote.bid_items?.some(bidItem => 
          bidItem.bid?.bid_options?.some(option => {
            return option.shortlisted == true || option.shortlisted === 1;
          })
        );
        return hasShortlisted;
      });
      
      // Transform API response to include id and status fields
      const transformedQuotes = filteredQuotes.map(quote => ({
        ...quote,
        id: quote.quote_id,
        status: quote.status || 'draft' // Default to 'draft' if no status
      }));
      
      // Client-side filter for draft to include null status
      if (activeFilter === 'draft') {
        return transformedQuotes.filter(quote => 
          quote.status === 'DRAFT' || !quote.status || quote.status === null
        );
      }
      
      return transformedQuotes;
    },
    enabled: !!profileId,
    staleTime: 0, // Always consider data stale
    refetchOnMount: 'always', // Refetch every time the component mounts
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  // Fetch all quotes for status counts
  const { data: allQuotesData } = useQuery({
    queryKey: ['quotes', 'all', profileId],
    queryFn: async () => {
      const data = await apiClient.getQuotes(0, 100, { created_by: profileId });
      return data.filter(quote =>
        quote.bid_items?.some(bidItem =>
          bidItem.bid?.bid_options?.some(option =>
            option.shortlisted == true || option.shortlisted === 1
          )
        )
      ).map(quote => ({
        ...quote,
        id: quote.quote_id,
        status: quote.status || 'draft'
      }));
    },
    enabled: !!profileId,
    staleTime: 30000,
  });

  const statusCounts = useMemo(() => {
    if (!allQuotesData) return {};
    const counts = { all: allQuotesData.length };
    allQuotesData.forEach(q => {
      const s = (q.status || 'draft').toLowerCase();
      counts[s] = (counts[s] || 0) + 1;
    });
    return counts;
  }, [allQuotesData]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  /**
   * Handle viewing a quote
   */
  const handleViewQuote = (quote) => {
    setSelectedQuoteId(quote.id);
    setIsDrawerOpen(true);
  };

  return (
    <div className="pb-20">

      {/* Main Content */}
      <div className="max-w-full">
        <Card className="overflow-hidden !border-gray-100 !bg-transparent !shadow-none !border-0">
          <CardContent className="!p-1">
            {/* Filter Section */}
            <div className="mb-6 flex justify-center w-full max-w-2xl mx-auto">
              <QuoteFilters
                activeFilter={activeFilter}
                onFilterChange={handleFilterChange}
                totalCount={quotesData?.length}
                statusCounts={statusCounts}
              />
            </div>

            {isQuotesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-11 w-11 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                      <Skeleton className="h-12 w-full" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-20" />
                      </div>
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : quotesData?.length === 0 ? (
              <Card className="max-w-md mx-auto !bg-transparent !border-0 !shadow-none">
                <CardContent className="p-12 text-center space-y-4">
                  <img src={EmptyState} alt="No quotes" className="w-30 h-30 mx-auto" />
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-2">No quotes created yet</h3>
                    <p className="text-sm text-gray-500">
                      {activeFilter === 'all'
                        ? <>Add aircraft to your shortlist to compare <br /> options and send a quote to your client.</>
                        : `No ${activeFilter} quotes at the moment.`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4">
                {quotesData?.map((quote) => (
                  <QuoteCard key={quote.id} quote={quote} onViewQuote={handleViewQuote} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quote Details Drawer */}
      <QuoteDetailsDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        quoteId={selectedQuoteId}
      />
    </div>
  );
};

export default QuotesList;